import { PaymentRequest } from '@stripe/stripe-js';
import {
  App,
  CheckoutResult,
  SubscriptionPlan,
  TrialPeriodDays,
  TrialPlan,
} from 'hydra-data-types';
import { Merge } from 'ts-utils';
import { DEFAULT_PURCHASE_ITEM, purchaseItemFromQueryParam } from '../../businessLogic/PlansData';
import { LoadedState, LoadingAndErrorState } from '../LoadingAndErrorState';

// Processing a purchase is complex as it goes through multiple stages.
// The typing of the `PurchaseState` has been setup to ensure the state is valid at each stage.
export type InitialPurchaseState = {
  plan: SubscriptionPlan;
  trialPlan: TrialPlan;
  trialPeriodDays: TrialPeriodDays;
  intent: {
    checkoutId: undefined;
    clientSecret: undefined;
    stripeAccount: undefined;
  } & LoadingAndErrorState;
  expressPay: { paymentRequest: undefined } & LoadingAndErrorState;
  payment: LoadingAndErrorState;
  checkoutId: undefined;
  addonPlan: string | undefined;
  /** The product to be used in a post-purchase up-sell, used as an override for ApiClient */
  addonProduct: App | undefined;
  /** The value of the plan that was first purchased, used to display in a post-purchase screen */
  purchasedPlanPrice: string | undefined;
  /** Set to true when an upsell is successfully purchased */
  addonPurchased: boolean | undefined;
};

export type ReadyPurchaseState = Merge<
  InitialPurchaseState,
  {
    intent: Pick<CheckoutResult, 'checkoutId' | 'clientSecret' | 'stripeAccount'> & LoadedState;
  }
>;

export type ReadyWithExpressPayPurchaseState = Merge<
  ReadyPurchaseState,
  { expressPay: { paymentRequest: PaymentRequest } & LoadedState }
>;

export type CompletedPurchaseState = Merge<
  ReadyPurchaseState | ReadyWithExpressPayPurchaseState,
  { payment: LoadedState; checkoutId: string }
>;

/** State relating to the checkout and payment processing. */
export type PurchaseState =
  | InitialPurchaseState
  | ReadyPurchaseState
  | ReadyWithExpressPayPurchaseState
  | CompletedPurchaseState;

export const INITIAL_PURCHASE_STATE: InitialPurchaseState = {
  ...(purchaseItemFromQueryParam() ?? DEFAULT_PURCHASE_ITEM),
  intent: {
    checkoutId: undefined,
    clientSecret: undefined,
    stripeAccount: undefined,
    loading: false,
    error: undefined,
  },
  expressPay: { paymentRequest: undefined, loading: false, error: undefined },
  payment: { loading: false, error: undefined },
  checkoutId: undefined,
  addonPlan: undefined,
  addonProduct: undefined,
  purchasedPlanPrice: undefined,
  addonPurchased: undefined,
};
